import { ReactNode, useCallback, useEffect, useState } from 'react';

/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';

import { PanelStateless } from '@atlaskit/panel';
import { Box, xcss } from '@atlaskit/primitives';

import atlassianStore from './atlassian-store';

type ExpandablePanelProps = {
  storageKey: string;
  header: ReactNode;
  children: ReactNode;
  defaultExpanded?: boolean;
  onExpandChange?: (newState: boolean) => void;
};

// Left padding is needed so that the panel icon doesn't overlap with side navigation
export const panelStyles = xcss({
  paddingLeft: 'space.300',
  width: '100%',
});

const ExpandablePanelWrapper = styled.div({
  // Set panel header content to fill width
  '> div > div > span': {
    width: '100%',
  },
  // Always show dropdown arrow
  '> div > div > div': {
    opacity: 1,
  },
});

const getDefaultExpanded = (storageKey: string): boolean => {
  return atlassianStore.get(storageKey) ?? true;
};

/**
 * Renders an expandable panel component with expanded state tracked in local storage.
 *
 * @param storageKey - The key used to store the expanded state in local storage.
 * @param header - The header content of the panel.
 * @param children - The panel's children elements representing its expanded content.
 * @param defaultExpanded - The default expanded state of the panel; overrides the local storage value.
 * @param onExpandChange - A callback function triggered when the expanded state changes.
 *
 * @returns The rendered expandable panel component.
 */
export const ExpandablePanel = ({
  storageKey,
  header,
  children,
  defaultExpanded = getDefaultExpanded(storageKey),
  onExpandChange,
}: ExpandablePanelProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Update the expanded state if the value of defaultExpanded is affected by
  // an async action
  // Ex. attachments loaded in async and should be expanded by default if any exist
  useEffect(() => {
    setIsExpanded(defaultExpanded);
  }, [defaultExpanded]);

  const onChange = useCallback(() => {
    setIsExpanded((prevState: boolean) => {
      const newState = !prevState;

      atlassianStore.setStrictlyNecessaryItem(storageKey, newState);

      if (onExpandChange) {
        onExpandChange(newState);
      }
      return newState;
    });
  }, [onExpandChange, storageKey]);

  return (
    <Box xcss={panelStyles}>
      <ExpandablePanelWrapper>
        <PanelStateless
          header={header}
          isExpanded={isExpanded}
          onChange={onChange}
        >
          {children}
        </PanelStateless>
      </ExpandablePanelWrapper>
    </Box>
  );
};
